'use client';

import color from '@haaretz/l-color.macro';
import merge from '@haaretz/l-merge.macro';
import mq from '@haaretz/l-mq.macro';
import radius from '@haaretz/l-radius.macro';
import space from '@haaretz/l-space.macro';
import typesetter from '@haaretz/l-type.macro';
import useRadioGroupState from '@haaretz/s-use-radio-group-state';
import * as React from 'react';
import s9 from 'style9';

import type { StyleExtend, InlineStyles } from '@haaretz/s-types';

const transitionDuration = '0.25s';
const outlineTransitionTime = '0.1s';

// `c` is short for `classNames`
const c = s9.create({
  wrapper: {
    '--bgc': color('neutral100'),
    alignItems: 'center',
    columnGap: space(2),
    cursor: 'pointer',
    display: 'inline-flex',
    position: 'relative',

    ':hover': {
      '--bgc': color('primary400'),
    },

    ':focus-within': {
      '--bgc': color('primary400'),
      '--outline-wdth': '2px',
      '--outline-offst': '3px',
    },
  },

  input: {
    pointerEvents: 'none',
    touchAction: 'none',
    height: 0,
    opacity: 0,
    // We need this so that the input isn't considered for
    // creating gaps in the wrapper
    position: 'absolute',
    width: 0,
  },
  radioButton: {
    aspectRatio: '1',
    backgroundColor: color('primary1000'),
    borderRadius: radius('circle'),
    flexGrow: 0,
    flexShrink: 0,
    outlineColor: color('primary1000', { opacity: 0.5 }),
    outlineOffset: 'var(--outline-offst, 0)',
    outlineStyle: 'solid',
    outlineWidth: 'var(--outline-wdth, 0)',
    position: 'relative',
    transitionDuration: outlineTransitionTime,
    transitionProperty: 'all',
    transitionTimingFunction: 'ease-in-out',
    width: space(4),

    ...merge(
      mq({
        from: 'xl',
        value: {
          width: space(5),
        },
      })
    ),

    ':after': {
      backgroundColor: 'var(--bgc)',
      borderRadius: radius('circle'),
      content: '""',
      height: 'calc(100% - 2px)',
      left: '50%',
      position: 'absolute',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      transitionDuration: `${transitionDuration}, ${transitionDuration}`,
      transitionProperty: 'height, width',
      transitionTimingFunction: 'ease-in-out',
      width: 'calc(100% - 2px)',
    },
  },
  disabledStateButton: {
    opacity: 0.25,
  },
  disabledStateText: {
    opacity: 0.5,
  },
  radioButtonChecked: {
    ':after': {
      height: space(2),
      width: space(2),
    },
  },
  disabled: {
    cursor: 'no-drop',
  },
  label: {
    color: color('bodyText'),
    ...typesetter(-2),
    ...merge(mq({ from: 'xxl', value: { ...typesetter(-3) } })),
  },
});

//////////////
//  SHARED  //
//////////////

export interface SharedProps {
  /**
   * CSS declarations to be set as inline `style` on the
   * html element.
   *
   * By setting values of CSS Custom Properties based on
   * props or state in the consuming component (where
   * the value of `inlineStyle` is passed), `inlineStyle`
   * can be used as an API contract for setting dynamic
   * values to styles created with `style9.create()`:
   *
   * @example
   * ```ts
   * import s9 from 'style9';
   * const { styleExtend, } = s9.create({
   *   styleExtend: {
   *     color: 'var(--color-based-on-prop)',
   *   },
   * });
   *
   * function MyRadioButton(props) {
   *   const inlineStyle = {
   *     '--color-based-on-prop': props.color,
   *   },
   *
   *   return (
   *    <RadioButton
   *      styleExtend={[ styleExtend, ]}
   *      inlineStyle={inlineStyle}
   *    />
   *   );
   * }
   * ```
   */
  inlineStyle?: InlineStyles;
  /**
   * An array of `Style`s created by `style9.create()`.
   * WARNING: **_do not_** pass simple CSS-in-JS object.
   * The items in the array must be created with Style9's
   * `create` function.
   * The array can also hold falsy values to assist with
   * conditional inclusion of `Style`s:
   *
   * @example
   * ```ts
   * const { foo, bar, } = s9.create({ foo: { ... }, bar: { ... }, });
   * <RadioButton styleExtend={[ someCondition && foo, bar, ]} />
   * ```
   */
  styleExtend?: StyleExtend;
  textStyleExtend?: StyleExtend;
  circleStyleExtend?: StyleExtend;
  /**
   * The functional state of the component.
   * @defaultValue 'enabled'
   */
  state?: 'disabled';
}

///////////////
//  Wrapper  //
///////////////

interface RadioButtonWrapperProps extends SharedProps, React.ComponentPropsWithoutRef<'label'> {
  /**
   * A refference to the `input` element that is rendered inside the wrapper.
   */
  inputRef: React.ForwardedRef<HTMLInputElement>;
}

export const RadioButtonWrapper = React.forwardRef<HTMLLabelElement, RadioButtonWrapperProps>(
  function RadioButtonWrapper(
    { children, inlineStyle, inputRef, styleExtend = [], onClick, onKeyDown, state, ...attrs },
    ref
  ) {
    const isDisabled = state === 'disabled';

    return (
      // eslint-disable-next-line max-len
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions
      <label
        {...attrs}
        className={s9(c.wrapper, isDisabled && c.disabled, ...styleExtend)}
        style={inlineStyle}
        onClick={onClick}
        onKeyDown={onKeyDown}
        ref={ref}
      >
        {children}
      </label>
    );
  }
);

////////////////////////
//  Description text  //
////////////////////////

export interface RadioButtonTextProps extends React.HTMLAttributes<HTMLDivElement> {
  // Make `id` required for A11Y
  id: string;
  state?: SharedProps['state'];
  textStyleExtend?: SharedProps['textStyleExtend'];
}

export const RadioButtonText = React.forwardRef<HTMLSpanElement, RadioButtonTextProps>(
  function RadioButtonText({ id, children, state, textStyleExtend = [], ...attrs }, ref) {
    const isDisabled = state === 'disabled';
    return (
      <span
        {...attrs}
        className={s9(c.label, isDisabled && c.disabledStateText, ...textStyleExtend)}
        id={id}
        ref={ref}
      >
        {children}
      </span>
    );
  }
);

///////////////////
//  RadioButton  //
///////////////////

type OmittedAttributes =
  | 'aria-labelledBy'
  | 'children'
  | 'defaultValue'
  | 'disabled'
  | 'readOnly'
  | 'required'
  | 'type'
  | 'value';

type InputBaseProps = React.ComponentPropsWithoutRef<'input'>;
export interface InputOwnProps {
  /**
   * Set the `checked` state of the radioButton or toggle.
   * @defaultValue false
   */
  checked?: boolean;
  /**
   * An ID of the element describing the radioButton or toggle
   */
  labelledBy?: string;
  /**
   * The value of the radio button when it is selected
   */
  value: NonNullable<InputBaseProps['value']>;
  /** Mark the radio button as required */
  isRequired?: boolean;
}
/* eslint-disable @typescript-eslint/indent */
interface InputProps
  extends SharedProps,
    InputOwnProps,
    Omit<
      InputBaseProps,
      OmittedAttributes | 'inlineStyle' | 'styleExtend'
      /* eslint-enable @typescript-eslint/indent */
    > {}

export type FauxRadioButtonProps = InputProps;

export const FauxRadioButton = React.forwardRef<HTMLInputElement, FauxRadioButtonProps>(
  function FauxRadioButton(
    {
      name,
      defaultChecked,
      labelledBy,
      isRequired,
      state,
      value,
      circleStyleExtend = [],
      ...attrs
    },
    ref
  ) {
    const inputRef = React.useRef<HTMLInputElement | null>(null);

    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const isDisabled = state === 'disabled';
    const isChecked = !!attrs.checked;

    const attrsOverride = getAttrsOverride({ labelledBy, state });

    if (!name) {
      console.error(
        '<RadioButton>: the `name` prop is required for a radio button to work as expected. Either pass it directly or use a `<ButtonGrouop>`'
      );
    }

    return (
      <>
        <input
          name={name}
          {...attrs}
          {...attrsOverride}
          required={isRequired}
          className={s9(c.input)}
          defaultChecked={defaultChecked}
          disabled={isDisabled}
          value={value}
          ref={inputRef}
        />
        <div
          aria-hidden="true"
          className={`${s9(
            c.radioButton,
            isChecked && c.radioButtonChecked,
            isDisabled && c.disabled,
            isDisabled && c.disabledStateButton,
            ...circleStyleExtend
          )} ${attrs.className || ''}`}
        />
      </>
    );
  }
);

/////////////////////////
//  Default Component  //
/////////////////////////

/* eslint-disable @typescript-eslint/indent */
export type RadioButtonProps = SharedProps & {
  /** Mark the radio button as required */
  isRequired?: boolean;
} & Omit<
    InputOwnProps & Omit<React.ComponentPropsWithoutRef<'input'>, 'value'>,
    'labelledBy' | 'type' | 'required'
    /* eslint-enable @typescript-eslint/indent */
  >;

const RadioButton = React.forwardRef<HTMLInputElement, RadioButtonProps>(function RadioButton(
  {
    inlineStyle,
    checked,
    children,
    styleExtend = [],
    textStyleExtend = [],
    circleStyleExtend,
    isRequired: isRequiredProp,
    state: stateProp,
    ...attrs
  }: RadioButtonProps,
  ref
) {
  const radioButtonId = React.useId();
  const labelId = React.useId();
  const inputRef = React.useRef<HTMLInputElement>(null);

  const {
    checkedElement,
    firstRadioButton,
    isChecked,
    isRequired,
    name,
    onChange,
    tabIndex,
    state: stateFromGroup,
  } = useRadioGroupState({
    checked,
    defaultChecked: attrs.defaultChecked,
    isRequired: isRequiredProp,
    input: inputRef.current,
  });

  const state = stateProp || stateFromGroup;
  const stateForSubComponents = state === 'disabled' ? 'disabled' : undefined;

  const wrapperOnClick: React.MouseEventHandler<HTMLLabelElement> = React.useCallback(
    evt => {
      if (state === 'read-only') {
        // focus focusable radio button
        isChecked
          ? inputRef.current?.focus()
          : checkedElement
          ? checkedElement.focus()
          : firstRadioButton
          ? firstRadioButton.focus()
          : inputRef.current?.focus();
      }

      // Nothing further should happen if the element is disabled read-only
      if (['disabled', 'read-only'].includes(state || '')) evt.preventDefault();
    },
    [checkedElement, firstRadioButton, isChecked, state]
  );

  const wrapperOnKeyDown: React.KeyboardEventHandler<HTMLLabelElement> = React.useCallback(
    evt => {
      const isMovementKey = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(evt.key);
      const isSelectionKey = [' ', 'Enter', 'Spacebar'].includes(evt.key);

      // prevent selection changes when read-only
      if (state === 'read-only' && (isMovementKey || isSelectionKey)) evt.preventDefault();
    },
    [state]
  );

  return (
    <RadioButtonWrapper
      style={inlineStyle}
      styleExtend={styleExtend}
      inputRef={ref || inputRef}
      onClick={wrapperOnClick}
      onKeyDown={wrapperOnKeyDown}
      state={stateForSubComponents}
    >
      <FauxRadioButton
        {...attrs}
        checked={isChecked}
        isRequired={isRequired}
        name={attrs.name || name}
        onChange={onChange}
        tabIndex={tabIndex}
        id={radioButtonId}
        labelledBy={labelId}
        state={stateForSubComponents}
        ref={ref || inputRef}
        circleStyleExtend={circleStyleExtend}
      />
      <RadioButtonText id={labelId} state={stateForSubComponents} textStyleExtend={textStyleExtend}>
        {children}
      </RadioButtonText>
    </RadioButtonWrapper>
  );
});

export default RadioButton;

/////////////
//  Utils  //
/////////////

type OverrideAttrs = 'labelledBy' | 'state';
type RadioButtonOverrideAttrNames = 'aria-labelledby' | 'type' | 'tabIndex' | 'disabled';
type RadioButtonOverrideAttrs = Pick<InputBaseProps, RadioButtonOverrideAttrNames>;

function getAttrsOverride({ labelledBy, state }: Pick<InputProps, OverrideAttrs>) {
  const attrsOverride: RadioButtonOverrideAttrs = {
    type: 'radio',
  };

  if (labelledBy) attrsOverride['aria-labelledby'] = labelledBy;
  if (state === 'disabled') {
    attrsOverride.tabIndex = undefined;
    attrsOverride.disabled = true;
  }

  return attrsOverride;
}
